// Content of the Help page. Add a guide or a question here; no code changes
// needed. Text in **double stars** is shown bold.
//
// Videos and posters live in S3 (app-related-images-prod, help/videos/) and
// are uploaded separately from the app. Recordings are made with dummy data
// on a local copy of the app (tools/video-rec); a guide can also be steps
// only (no `video`), or open its own page (`page`).

export const MEDIA_BASE = process.env.REACT_APP_HELP_MEDIA_BASE
  || 'https://app-related-images-prod.s3.ap-south-1.amazonaws.com/help/videos/';

export const GUIDES = [
  {
    id: 'disbursement-process',
    title: 'How to disburse a loan through BrickEZ',
    summary: 'The whole process in 7 steps: from adding the loan to releasing each tranche.',
    // Two ways in: the video (opens here) and the step-by-step page.
    page: '/help/disbursement-process', // screens/Help/DisbursementProcess
    video: { file: 'how-to-disburse-a-loan.mp4', poster: 'how-to-disburse-a-loan.jpg', duration: '1:20' },
    steps: [
      '**Search for the loan** in Loans.',
      "**Add the loan** if it isn't there — with structure type, floors and GPS coordinates.",
      '**Generate the construction plan** that matches the structure and floors.',
      '**Record past progress**: verify work already completed in the last valuation report.',
      '**Record past disbursements** with their stage %.',
      '**Customer** uploads progress photos in the app and requests the next disbursement; you **verify** the work.',
      '**Download the reports**, review them, release the amount and update the request.',
    ],
  },
  {
    id: 'add-loan',
    title: 'Add a loan',
    summary: 'Add a customer\'s loan and property, then review and confirm.',
    video: { file: 'how-to-add-a-loan.mp4', poster: 'how-to-add-a-loan.jpg', duration: '1:25' },
    steps: [
      'Open **Loans** from the menu on the left.',
      'Click **Add Loan**. The form opens on the right.',
      '**Customer**: enter the first and last name and the mobile number. Email is optional.',
      '**Loan**: pick the loan type and the branch (type to search), then enter the loan number.',
      'Enter the **sanctioned amount**, **sanctioned date** and **LTV**. Land, construction and property value are optional.',
      '**Property**: pick the structure type and number of floors, and enter the built-up area.',
      '**Address & GPS coordinates**: enter the address and the latitude and longitude of the site.',
      'Click **Review**, check the details and the pin on the map, then click **Confirm & add loan**. **Back to edit** keeps everything you typed.',
    ],
    tip: 'Fields marked * are required. If one is missing, Review takes you to it.',
  },
  {
    id: 'update-gps',
    title: 'Update a property\'s GPS location',
    summary: 'Fix the latitude and longitude so the customer can upload site photos.',
    video: { file: 'update-gps-location.mp4', poster: 'update-gps-location.jpg', duration: '0:45' },
    steps: [
      'Open **Loans** from the menu on the left.',
      'Search for the loan and click the **pencil** icon on its row.',
      'In **GPS coordinates**, enter the correct **latitude** and **longitude** — from the valuation report or Google Maps. In India latitude is 6–37 and longitude 68–98.',
      'Click **Review changes**: check the before → after values and that the pin is on the property.',
      'Click **Save changes**.',
    ],
    tip: 'Wrong coordinates stop the customer from uploading site photos and videos.',
  },
  {
    id: 'generate-plan',
    title: 'Generate the construction plan',
    summary: 'Create the activities the customer reports progress on.',
    video: { file: 'generate-construction-plan.mp4', poster: 'generate-construction-plan.jpg', duration: '0:38' },
    steps: [
      'Open **Loans**, search for the loan and click the customer\'s name.',
      'In **Property**, check the **structure type** and **number of floors** — the plan must match them.',
      'In **Construction Plan**, click **Generate Plan**.',
      'Choose the plan that matches the property and click **Update**.',
      'The plan\'s activities and their weightage appear on the loan.',
    ],
    tip: '**Reset Plan** replaces the current plan and removes the progress submitted on it — use it only when the plan is wrong.',
  },
  {
    id: 'verify-progress',
    title: 'Verify submitted construction progress',
    summary: 'Check the customer\'s photos and mark the work as verified.',
    video: { file: 'verify-construction-progress.mp4', poster: 'verify-construction-progress.jpg', duration: '0:49' },
    steps: [
      'Open **Loans**, search for the loan and click the customer\'s name.',
      'In **Construction Plan**, activities waiting for you show **Submitted**. Click the activity\'s name.',
      'Check the photos, and that **Media clicked location** matches the property.',
      'Click **Change Status**, choose **Verify**, add a remark and click **Update**.',
      'To send it back, choose **Reject** instead — the customer uploads the photos again.',
    ],
    tip: 'Verify or reject every Submitted activity before downloading the progress report.',
  },
  {
    id: 'progress-report',
    title: 'Download the construction progress report',
    summary: 'Pick the photos, then review and download the report.',
    video: { file: 'download-progress-report.mp4', poster: 'download-progress-report.jpg', duration: '0:58' },
    steps: [
      'Open **Loans**, search for the loan and click the customer\'s name.',
      'In **Construction Plan**, click **Download Report**.',
      'Choose the report format and click **Choose photos**.',
      'Pick up to **6 photos** — they appear in the report in the order you pick them — and click **Generate report**.',
      'Review the report, then download or print it from the toolbar at the top right. The BCM adds remarks.',
    ],
  },
  {
    id: 'drf',
    title: 'Download the Disbursement Request Form',
    summary: 'For a customer\'s open disbursement request.',
    video: { file: 'download-drf.mp4', poster: 'download-drf.jpg', duration: '0:43' },
    steps: [
      'Open **Loans**, search for the loan and click the customer\'s name.',
      'In **Disbursal Requests**, click the request that shows **Open**.',
      'Click **Download DRF**, keep the form format and click **Generate report**.',
      'Review the form, then download or print it from the toolbar at the top right.',
      'After releasing the amount, click **Change Status** on the request — the customer is notified.',
    ],
    tip: 'The form can be downloaded only while the request is Open.',
  },
];

export const FAQS = [
  {
    q: 'Why can\'t a customer upload site photos or videos?',
    a: 'Photos and videos are accepted only when they are taken at the property. The app checks where each photo was taken against the loan\'s **latitude and longitude**. If those are wrong or missing, the upload is refused. Open the loan, check Latitude and Longitude under **Property**, and correct them with **Edit**.',
  },
  {
    q: 'Where do I get the latitude and longitude?',
    a: 'In **Google Maps**, find the exact site, then right-click it (on a phone, press and hold). The two numbers shown are latitude, then longitude — for example 10.8262, 78.6857. In India latitude is between 6 and 37 and longitude between 68 and 98; if yours are the other way round, they are swapped.',
  },
  {
    q: 'Why don\'t I see a loan in the list?',
    a: 'You see loans only for the branches, cities or regions your access covers. If a loan should be visible to you, ask your administrator to check your access level.',
  },
  {
    q: 'Why is a branch missing when I add a loan?',
    a: 'The branch list shows only the branches you have access to. Type part of the branch name to search. If it is still missing, ask your administrator.',
  },
  {
    q: 'What is LTV?',
    a: '**Loan-to-value**: the loan amount as a percentage of the property value. Enter a number from 0 to 100 — for example 70 for 70%.',
  },
  {
    q: 'It says the loan number already exists.',
    a: 'Each loan number can be used only once in your organisation. Search **Loans** for that number — the loan may already have been added.',
  },
  {
    q: 'What do the activity statuses mean?',
    a: '**Yet to start** — nothing uploaded yet. **Uploading** — photos or videos are being sent from the app. **Submitted** — uploaded and waiting for review. **Verified** — reviewed and accepted. **Rejected** — reviewed and sent back; the customer needs to upload again.',
  },
  {
    q: 'What does the Maps page show?',
    a: 'Every loan with a location, as a pin coloured by recent photo activity. A ring around a pin means it needs attention — for example photos taken away from the site, activities waiting for review, or an open disbursement request. Click a pin to see the loan.',
  },
  {
    q: 'Who can add or edit loans?',
    a: 'It depends on your role. Your administrator decides what each role can view, add and edit.',
  },
];
